import { useState, useEffect, useRef } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Collection } from '@/types/models'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useLongPress } from '@/hooks/useLongPress'
import { Trash2, Edit } from 'lucide-react'
import { cn } from '@/lib/utils'
import { deleteCollection, renameCollection } from '@/lib/collectionData'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useAuthState } from '@/hooks/useAuth'

interface CollectionPreviewProps {
  collection: Collection
  onClick: () => void
}

interface PreviewImage {
  url: string
  id: string
}

// Simple in-memory cache for collection preview images
const imageCache = new Map<string, PreviewImage[]>()

export function CollectionPreview({ collection, onClick }: CollectionPreviewProps) {
  const { user } = useAuthState()
  const [previewImages, setPreviewImages] = useState<PreviewImage[]>([])
  const [loading, setLoading] = useState(true)
  const [isVisible, setIsVisible] = useState(false)
  const [showActionOverlay, setShowActionOverlay] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const [renameTitle, setRenameTitle] = useState(collection.title)
  const [isRenaming, setIsRenaming] = useState(false)
  const collectionRef = useRef<HTMLDivElement>(null)
  const [collectionPosition, setCollectionPosition] = useState({ top: 0, left: 0, width: 0, height: 0 })
  const [collectionSnapshot, setCollectionSnapshot] = useState<{ html: string; className: string } | null>(null)

  useEffect(() => {
    async function fetchPreviewImages() {
      // Check cache first
      const cacheKey = collection.id
      if (imageCache.has(cacheKey)) {
        setPreviewImages(imageCache.get(cacheKey)!)
        setLoading(false)
        setIsVisible(true)
        return
      }

      try {
        setLoading(true)
        
        // Get first 4 items from the collection
        const itemsToFetch = collection.items.slice(0, 4)
        
        const imagePromises = itemsToFetch.map(async (item) => {
          const collectionName = item.type === 'flash' ? 'flashes' : 'portfolioItems'
          const itemRef = doc(db, collectionName, item.id)
          const itemDoc = await getDoc(itemRef)
          
          if (itemDoc.exists()) {
            const data = itemDoc.data()
            return {
              url: data.compressedImageUrl || data.imageUrl,
              id: item.id
            }
          }
          
          return null
        })
        
        const images = await Promise.all(imagePromises)
        const filteredImages = images.filter(Boolean) as PreviewImage[]
        
        // Store in cache
        imageCache.set(cacheKey, filteredImages)
        
        setPreviewImages(filteredImages)
        setLoading(false)
        setIsVisible(true)
      } catch (err) {
        console.error('Error fetching preview images:', err)
        setLoading(false)
        setIsVisible(true)
      }
    }

    if (collection.items.length > 0) {
      fetchPreviewImages()
    } else {
      setLoading(false)
      setIsVisible(true)
    }
  }, [collection.id, collection.items])

  // Only allow long press for own collections
  const isOwnCollection = user?.uid === collection.userId

  const handleLongPress = () => {
    if (!isOwnCollection) return

    // Capture position and snapshot of the collection
    if (collectionRef.current) {
      const rect = collectionRef.current.getBoundingClientRect()
      setCollectionPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      })
      
      // Capture the collection element snapshot
      setCollectionSnapshot({
        html: collectionRef.current.innerHTML,
        className: collectionRef.current.className
      })
    }
    
    setShowActionOverlay(true)
  }

  // Determine button alignment based on collection position
  const getButtonAlignment = () => {
    const screenWidth = window.innerWidth
    const collectionCenter = collectionPosition.left + collectionPosition.width / 2
    const leftThird = screenWidth / 3

    if (collectionCenter < leftThird) {
      return 'left'
    } else {
      // Middle and right both use 'right' positioning
      return 'right'
    }
  }

  const buttonAlignment = getButtonAlignment()
  
  // Calculate button container position based on alignment
  const getButtonContainerStyle = () => {
    const baseTop = collectionPosition.top + collectionPosition.height + 16
    const padding = 16 // padding from screen edges
    
    if (buttonAlignment === 'left') {
      return {
        top: `${baseTop}px`,
        left: `${collectionPosition.left}px`,
      }
    } else {
      // Right and middle - align to the right, but ensure they don't go off-screen
      const rightValue = window.innerWidth - (collectionPosition.left + collectionPosition.width) - padding
      return {
        top: `${baseTop}px`,
        right: `${Math.max(padding, rightValue)}px`,
      }
    }
  }

  const handleDelete = async () => {
    if (!collection.id) return
    
    setIsDeleting(true)
    try {
      await deleteCollection(collection.id)
      // Collection will be removed automatically via Firestore listener
      setShowActionOverlay(false)
    } catch (error) {
      console.error('Failed to delete collection:', error)
      setIsDeleting(false)
    }
  }

  const handleRename = () => {
    setShowActionOverlay(false)
    setShowRenameDialog(true)
  }

  const handleRenameSubmit = async () => {
    if (!collection.id || !renameTitle.trim()) return
    
    setIsRenaming(true)
    try {
      await renameCollection(collection.id, renameTitle)
      setShowRenameDialog(false)
    } catch (error) {
      console.error('Failed to rename collection:', error)
    } finally {
      setIsRenaming(false)
    }
  }

  const handleCancelAction = () => {
    setShowActionOverlay(false)
  }

  const handleCancelRename = () => {
    setShowRenameDialog(false)
    setRenameTitle(collection.title)
  }

  // Long press handlers for action functionality
  const actionLongPressHandlers = useLongPress({
    onLongPress: handleLongPress,
    onClick: () => {
      // Only trigger onClick if not in action overlay mode
      if (!showActionOverlay) {
        onClick()
      }
    },
    ms: 500
  })

  // Determine layout: single image for 3 or fewer items, grid for more
  const isSingleImage = previewImages.length <= 3

  return (
    <>
    <div 
      ref={collectionRef}
      className="cursor-pointer select-none" 
      {...(isOwnCollection ? actionLongPressHandlers : { onClick })}
    >
      <div className={`aspect-square rounded-lg overflow-hidden hover:opacity-90 transition-opacity bg-muted ${isVisible && !loading ? 'animate-in fade-in duration-300' : ''}`}>
        {loading ? (
          // Clean shimmer skeleton loader
          <div className="w-full h-full bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
        ) : collection.coverImageUrl ? (
            <div className="w-full h-full">
              <img
                src={collection.coverImageUrl}
                alt={`${collection.title} cover`}
                className="w-full h-full object-cover select-none"
                draggable="false"
              />
            </div>
          ) : previewImages.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <p className="text-sm font-medium text-muted-foreground truncate px-2">
              Empty
            </p>
          </div>
          ) : isSingleImage ? (
          // Single image layout for 3 or fewer items
          <div className="w-full h-full">
            <img 
              src={previewImages[0].url} 
              alt={`Preview 1`}
              className="w-full h-full object-cover select-none"
              draggable="false"
            />
          </div>
        ) : (
          // 2x2 grid layout for more than 3 items - no gaps, clean edges
          <div className="w-full h-full grid grid-cols-2 grid-rows-2">
            {previewImages.slice(0, 4).map((image, index) => (
              <div 
                key={image.id} 
                className="relative aspect-square overflow-hidden bg-background"
              >
                <img 
                  src={image.url} 
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover select-none"
                  draggable="false"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      <p className={`text-sm font-medium mt-1 truncate ${isVisible && !loading ? 'animate-in fade-in duration-300' : ''}`}>{collection.title}</p>
      <p className={`text-xs text-muted-foreground ${isVisible && !loading ? 'animate-in fade-in duration-300' : ''}`}>
        {collection.itemCount || 0} {collection.itemCount === 1 ? 'item' : 'items'}
      </p>
    </div>

    {/* Instagram-style Blur Overlay with Delete/Rename Options - Using Portal */}
    {showActionOverlay && createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50"
          style={{ 
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            margin: 0
          }}
        >
          {/* Blur backdrop - covers entire viewport */}
          <div
            onClick={handleCancelAction}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          
          {/* Clone of the collection above the blur - unblurred */}
          {collectionSnapshot && (
            <motion.div
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ 
                duration: 0.3,
                times: [0, 0.5, 1],
                ease: "easeInOut"
              }}
              className={collectionSnapshot.className}
              style={{
                position: 'absolute',
                top: collectionPosition.top,
                left: collectionPosition.left,
                width: collectionPosition.width,
                height: collectionPosition.height,
                zIndex: 60,
                pointerEvents: 'none'
              }}
              dangerouslySetInnerHTML={{ __html: collectionSnapshot.html }}
            />
          )}
          
          {/* Action buttons positioned below the collection */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="absolute flex gap-3"
            style={getButtonContainerStyle()}
          >
            <button
              onClick={handleRename}
              disabled={isDeleting}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-[18px]",
                "bg-background text-foreground font-semibold text-sm",
                "hover:bg-muted active:scale-95",
                "transition-all shadow-lg",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Edit className="w-5 h-5" />
              <span>Rename</span>
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-[18px]",
                "bg-background text-red-500 font-semibold text-sm",
                "hover:bg-muted active:scale-95",
                "transition-all shadow-lg",
                "disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              <Trash2 className="w-5 h-5" />
              <span>{isDeleting ? 'Deleting...' : 'Delete'}</span>
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body
    )}

    {/* Rename Dialog */}
    {showRenameDialog && createPortal(
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center"
        >
          {/* Backdrop */}
          <div
            onClick={handleCancelRename}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />
          
          {/* Dialog */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative z-60 bg-background rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl"
          >
            <h2 className="text-lg font-semibold mb-4">Rename Collection</h2>
            <Input
              type="text"
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder="Collection title"
              className="mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameSubmit()
                }
              }}
            />
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={handleCancelRename}
                disabled={isRenaming}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameSubmit}
                disabled={isRenaming || !renameTitle.trim()}
              >
                {isRenaming ? 'Renaming...' : 'Rename'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>,
      document.body
    )}
    </>
  )
}

